import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { bannerUrl, HERO_BG } from "../../lib/constants";

const SLIDE_W = 84;
const EDGE = (100 - SLIDE_W) / 2;
const INTERVAL = 4000;
const SPEED = 700;

// Normalize whatever field names your CMS uses for hero banners.
const norm = (b) => ({
  id: b.id,
  src: b.image_url || b.image_path || HERO_BG,
  link: b.cta_link || b.link || "/catalog",
  alt: b.alt || b.title || "Swayambhu Gold",
});

function renderImg(b) {
  const img = (
    <img src={bannerUrl(b.src)} alt={b.alt} className="w-full h-full object-cover" draggable={false} />
  );
  if (!b.link) return img;
  return /^https?:\/\//i.test(b.link)
    ? <a href={b.link} className="block w-full h-full">{img}</a>
    : <Link to={b.link} className="block w-full h-full">{img}</Link>;
}

function SingleHero({ banner }) {
  return (
    <section className="relative bg-pearl py-5 lg:py-7 overflow-hidden" data-testid="hero-banner">
      <div className="px-4 lg:px-12">
        <div className="relative aspect-[8/3] w-full overflow-hidden rounded-2xl bg-line/30">
          {renderImg(banner)}
        </div>
      </div>
    </section>
  );
}

function LoopCarousel({ slides }) {
  const n = slides.length;
  const view = [slides[n - 1], ...slides, slides[0], slides[1 % n]];
  const [pos, setPos] = useState(1);
  const [anim, setAnim] = useState(true);
  const timer = useRef(null);

  // Auto-advance, but pause when the tab is hidden.
  useEffect(() => {
    const start = () => {
      stop();
      timer.current = setInterval(() => setPos((p) => p + 1), INTERVAL);
    };
    const stop = () => {
      if (timer.current) { clearInterval(timer.current); timer.current = null; }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Coming back into view: snap cleanly to a real slide, then resume.
        setAnim(false);
        setPos((p) => ((p - 1) % n + n) % n + 1);
        start();
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [n]);

  const onEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    // Snap back whenever we've moved onto either clone edge.
    if (pos === n + 1) { setAnim(false); setPos(1); }
    else if (pos === 0) { setAnim(false); setPos(n); }
  };

  useEffect(() => {
    if (anim) return;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
    return () => cancelAnimationFrame(id);
  }, [anim]);

  const activeDot = ((pos - 1) % n + n) % n;
  const goTo = (i) => { setAnim(true); setPos(i + 1); };

  return (
    <section className="relative bg-pearl py-5 lg:py-7 overflow-hidden" data-testid="hero-banner">
      <div className="overflow-hidden">
        <div
          className="flex"
          style={{
            transform: `translateX(calc(${EDGE}% - ${pos * SLIDE_W}%))`,
            transition: anim ? `transform ${SPEED}ms ease-out` : "none",
          }}
          onTransitionEnd={onEnd}
        >
          {view.map((b, i) => (
            <div key={i} className="shrink-0 w-[84%] px-2 lg:px-3" data-testid={`hero-slide-${i}`}>
              <div className="relative aspect-[8/3] w-full overflow-hidden rounded-2xl bg-line/30">
                {renderImg(b)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center gap-2 mt-5">
        {slides.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} aria-label={`Go to banner ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className={`h-2 rounded-full transition-all ${i === activeDot ? "w-7 bg-maroon" : "w-2 bg-line hover:bg-gold/60"}`} />
        ))}
      </div>
    </section>
  );
}

export default function HeroBanner() {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    // Pull hero banners from the SAME system your Site CMS writes to.
    api.get("/api/banners?kind=hero")
      .then((r) => setBanners((r.data || []).map(norm)))
      .catch(() => {});
  }, []);

  const real = banners.length
    ? banners
    : [{ id: "default", src: HERO_BG, link: "/catalog", alt: "Swayambhu Gold" }];

  return real.length <= 1 ? <SingleHero banner={real[0]} /> : <LoopCarousel slides={real} />;
}